import { Form } from "@models/form";
import { SiteProperties } from "@models/site";
import {
	Component,
	Element,
	Prop,
	State,
	getAssetPath,
	h,
} from "@stencil/core";
import { postAccessRequest } from "@utils/data";

type FormStage = "register" | "success";

@Component({
	tag: "contact-form",
	styleUrl: "contact-form.scss",
	scoped: true,
})
export class ContactForm {
	/**
	 * The Sitenna API Key, this is required to use the component.
	 * This availble through the Sitenna Platform.
	 * If you have issues finding it, please contact support@sitenna.com
	 * @required
	 */
	@Prop() sitennaApiKey: string;
	/**
	 * Api Endpoint
	 * @required
	 */
	@Prop() apiEndpoint: string;
	/**
	 * Site Properties
	 * @required
	 */
	@Prop() site!: SiteProperties;

	/**
	 * close modal callback
	 * @required
	 */
	@Prop() closeCallback!: () => void;

	@State() name = "";
	@State() email = "";
	@State() message = "";
	@State() organization = "";
	@State() phoneNumber = "";
	@State() intlCode = "1";
	@State() showModal = false;
	@State() errors: Record<string, string> = {};
	@State() formsStage: FormStage = "register";

	@Element() el: HTMLElement;

	private handleInputChange = (event: InputEvent) => {
		const { name, value } = event.target as HTMLInputElement;
		this[name] = value;
	};

	private validateForm = (): boolean => {
		const errors: Record<string, string> = {};

		if (!this.name.trim()) {
			errors.name = "Name is required";
		}

		if (!this.email.trim()) {
			errors.email = "Email is required";
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
			errors.email = "Email is not valid";
		}

		if (!this.phoneNumber.trim()) {
			errors.phoneNumber = "Phone Number is required";
		}

		if (!this.intlCode.trim()) {
			errors.intlCode = "Required";
		}

		if (!this.organization.trim()) {
			errors.organization = "Organization is required";
		}

		if (!this.message.trim()) {
			errors.message = "Message is required";
		}

		this.errors = errors;
		return Object.keys(errors).length === 0;
	};

	private handleSubmit = (event: Event) => {
		event.preventDefault();

		if (!this.validateForm()) {
			return;
		}

		const data: Form = {
			name: this.name,
			email: this.email,
			message: this.message,
			organization: this.organization,
			phoneNumber: `+${this.intlCode}${this.phoneNumber}`,
			site: this.site,
		};

		postAccessRequest(this.apiEndpoint, this.sitennaApiKey, data)
			.then((response) => {
				if (response.ok) {
					this.formsStage = "success";
				}
			})
			.catch((error) => {
				this.errors.formError =
					"Oops, something went wrong while submitting the form, please try again later.";
				console.error(error);
			});
	};

	get address() {
		return `${this.site.address1 ?? ""} ${this.site.address2 ?? ""}`;
	}

	private form() {
		return (
			<div>
				<div class="top-section">
					<div class="location">
						<div>
							<h2>{this.site.name}</h2>
							<span>{this.site.operator_site_id}</span>
						</div>
						<p>{this.address}</p>
					</div>
					{this.closeButton()}
				</div>
				<form onSubmit={this.handleSubmit}>
					<div class="input-group">
						<label htmlFor="name">Name*</label>
						<input
							type="text"
							name="name"
							value={this.name}
							onInput={this.handleInputChange}
							placeholder="Enter your name"
						/>
						{this.errors.name && <span class="error">{this.errors.name}</span>}
					</div>

					<div class="input-group">
						<label htmlFor="email">Email*</label>
						<input
							type="email"
							name="email"
							value={this.email}
							onInput={this.handleInputChange}
							placeholder="Enter your work email"
						/>
						{this.errors.email && (
							<span class="error">{this.errors.email}</span>
						)}
					</div>

					<div class="phone-group">
						<div class="input-group">
							<label htmlFor="intl-code">Intl code*</label>
							<div class="intl-wrapper">
								<input
									type="number"
									id="intl-code"
									name="intl-code"
									value={this.intlCode}
									onInput={(e) => {
										this.intlCode = (e.target as HTMLInputElement).value;
									}}
								/>
								<span>+</span>
							</div>
							{this.errors.intlCode && (
								<span class="error">{this.errors.intlCode}</span>
							)}
						</div>
						<div class="input-group">
							<label htmlFor="phoneNumber">Phone Number*</label>
							<input
								type="number"
								name="phoneNumber"
								value={this.phoneNumber}
								onInput={(e) => {
									this.phoneNumber = (e.target as HTMLInputElement).value;
								}}
								placeholder="Enter your phone number"
							/>
							{this.errors.phoneNumber && (
								<span class="error">{this.errors.phoneNumber}</span>
							)}
						</div>
					</div>

					<div class="input-group">
						<label htmlFor="organization">Organization*</label>
						<input
							type="text"
							name="organization"
							value={this.organization}
							onInput={this.handleInputChange}
							placeholder="Enter your organization name"
						/>
						{this.errors.organization && (
							<span class="error">{this.errors.organization}</span>
						)}
					</div>

					<div class="input-group">
						<label htmlFor="message">Message*</label>
						<textarea
							maxLength={1000}
							name="message"
							value={this.message}
							onInput={this.handleInputChange}
							placeholder="Enter your message to send to the site owner"
						/>
						{this.errors.message && (
							<span class="error">{this.errors.message}</span>
						)}
					</div>

					<div class="footer">
						<span class="required-warning">* required field</span>
						<button type="submit" id="submit-button">
							Submit
						</button>
					</div>
					{this.errors.formError && (
						<span class="form-error">{this.errors.formError}</span>
					)}
				</form>
			</div>
		);
	}

	private successMessage() {
		return (
			<div class="success-message">
				<h2>Thank you for your enquiry!</h2>
				<p>We will get back to you shortly.</p>
				<div class="button-container">
					<button
						type="submit"
						class="close-form-button"
						onClick={this.closeCallback}
					>
						Close
					</button>
				</div>
			</div>
		);
	}

	private closeButton() {
		return (
			<button type="submit" class="close-button" onClick={this.closeCallback}>
				<img src={getAssetPath("assets/icon-close.svg")} alt="close button" />
			</button>
		);
	}

	render() {
		return (
			<div class="content">
				{this.formsStage === "register" ? this.form() : this.successMessage()}
			</div>
		);
	}
}
